import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from './supabase';
import { getAuth, sendPasswordResetEmail, sendFirstAccessCode, trocarSenha } from './auth-compat';
import RequisitosSenha from './components/RequisitosSenha';
import { senhaForte } from './utils/senhaPolitica';

// Tela de "Nova senha" — chega aqui de três jeitos:
// 1) pelo link de e-mails de recuperação antigos: /nova-senha?token_hash=...&type=recovery
// 2) pelo Login, depois de pedir o código: /nova-senha?email=...&modo=recuperar|primeiro
//    (modo=primeiro → código do "Primeiro acesso", enviado pelo modelo codigo-acesso)
// 3) pelo App.js quando a senha venceu ou o admin marcou "Trocar agora":
//    /nova-senha?email=...&modo=expirada&enviado=1 (código de recuperação já enviado)
// A senha é gravada por api/trocar-senha (regras + não pode ser parecida com as anteriores).
// O token só é validado no clique em "Salvar" (não ao abrir a página), para que
// antivírus de e-mail que pré-abrem links não consumam o link antes da pessoa.

const inputStyle = { width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', boxSizing: 'border-box' };
const labelStyle = { fontWeight: 600, color: '#334155', display: 'block', marginBottom: '8px' };

function NovaSenha() {
    const [params] = useSearchParams();
    const tokenHash = params.get('token_hash');
    const viaLink = !!tokenHash && (params.get('type') || 'recovery') === 'recovery';
    const primeiroAcesso = params.get('modo') === 'primeiro';
    const expirada = params.get('modo') === 'expirada';

    const [email, setEmail] = useState(params.get('email') || '');
    const [codigo, setCodigo] = useState('');
    const [novaSenha, setNovaSenha] = useState('');
    const [confirmar, setConfirmar] = useState('');
    const [verificado, setVerificado] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [concluido, setConcluido] = useState(false);
    const [aviso, setAviso] = useState(params.get('enviado') === '1'
        ? (expirada
            ? 'Por segurança, sua senha precisa ser trocada. Enviamos um código para o seu e-mail — confira também a caixa de spam.'
            : 'Se este e-mail estiver cadastrado, o código chega em instantes. Confira também a caixa de spam.')
        : '');
    // Reenviar: o Supabase só aceita um envio por minuto para o mesmo e-mail
    const [espera, setEspera] = useState(params.get('enviado') === '1' ? 60 : 0);
    useEffect(() => {
        if (espera <= 0) return undefined;
        const t = setTimeout(() => setEspera(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [espera]);

    const reenviar = async () => {
        setError('');
        if (!email) { setError('Informe seu e-mail.'); return; }
        try {
            if (primeiroAcesso) await sendFirstAccessCode(email.trim().toLowerCase());
            else await sendPasswordResetEmail(getAuth(), email.trim().toLowerCase());
            setCodigo('');
            setAviso('Enviamos um novo código. Use só o código do e-mail mais recente — os anteriores deixam de valer.');
            setEspera(60);
        } catch (err) {
            setError(err.code === 'auth/too-many-requests' ? 'Aguarde 1 minuto antes de pedir outro código.' : 'Não foi possível reenviar agora. Tente de novo em instantes.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!viaLink && !verificado) {
            if (!email) { setError('Informe seu e-mail.'); return; }
            if (!/^\d{6}$/.test(codigo.trim())) { setError('O código tem 6 números — confira no e-mail.'); return; }
        }
        if (!senhaForte(novaSenha)) { setError('A senha ainda não atende a todos os requisitos da lista.'); return; }
        if (novaSenha !== confirmar) { setError('As senhas não coincidem.'); return; }

        setLoading(true);
        try {
            // 1. Valida o link/código (uma vez só — se a troca falhar, tenta de novo sem revalidar)
            if (!verificado) {
                localStorage.removeItem('qh_session_token');
                const { error: errOtp } = viaLink
                    ? await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' })
                    : await supabase.auth.verifyOtp({ email: email.trim().toLowerCase(), token: codigo.trim(), type: primeiroAcesso ? 'email' : 'recovery' });
                if (errOtp) {
                    setError(viaLink
                        ? 'Este link expirou ou já foi usado. Volte ao login e clique em "Esqueci minha senha" para receber outro.'
                        : 'Código inválido ou expirado. Confira se é o código do e-mail mais recente ou clique em "Reenviar código".');
                    return;
                }
                setVerificado(true);
            }

            // 2. Grava a senha nova pelo servidor (regras, histórico, data da troca e fim do "trocar agora")
            try {
                await trocarSenha(novaSenha);
            } catch (errUpd) {
                setError(errUpd.message);
                return;
            }

            setConcluido(true);
            setTimeout(() => { window.location.href = '/home'; }, 2000);
        } catch (err) {
            console.error(err);
            setError('Erro inesperado: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (concluido) {
        return (
            <div className="auth-wrapper">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <h2 style={{ color: '#0f172a', fontWeight: 800, marginBottom: 8 }}>{primeiroAcesso ? 'Senha criada!' : 'Senha alterada!'}</h2>
                    <p style={{ color: '#64748b' }}>Entrando no sistema...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-wrapper">
            <div className="auth-card">
                <h2 style={{ color: '#0f172a', fontWeight: 800, textAlign: 'center', marginBottom: '8px' }}>{primeiroAcesso ? 'Primeiro acesso' : expirada ? 'Troca de senha obrigatória' : 'Criar nova senha'}</h2>
                <p style={{ marginBottom: aviso ? '16px' : '32px', textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
                    {viaLink
                        ? 'Escolha sua nova senha de acesso ao Quality.'
                        : primeiroAcesso
                            ? 'Digite o código de 6 números que enviamos para o seu e-mail e crie sua senha de acesso.'
                            : 'Digite o código de 6 números que enviamos para o seu e-mail e escolha a nova senha.'}
                </p>
                {aviso && !verificado && (
                    <div style={{ color: expirada ? '#92400e' : '#166534', background: expirada ? '#fffbeb' : '#f0fdf4', border: `1px solid ${expirada ? '#fde68a' : '#bbf7d0'}`, borderRadius: 8, padding: '10px 12px', marginBottom: '24px', fontSize: '0.85rem' }}>{aviso}</div>
                )}

                <form onSubmit={handleSubmit}>
                    {!viaLink && !verificado && (
                        <>
                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={labelStyle}>E-mail</label>
                                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
                            </div>
                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={labelStyle}>Código recebido por e-mail</label>
                                <input
                                    type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                                    value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                                    required placeholder="000000"
                                    style={{ ...inputStyle, letterSpacing: '6px', fontSize: '1.2rem', textAlign: 'center' }}
                                />
                                <button
                                    type="button" onClick={reenviar} disabled={espera > 0}
                                    style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, fontSize: '0.82rem', cursor: espera > 0 ? 'default' : 'pointer', color: espera > 0 ? '#94a3b8' : '#4f46e5', fontWeight: 600 }}
                                >
                                    {espera > 0 ? `Não chegou? Reenviar em ${espera}s` : 'Não chegou? Reenviar código'}
                                </button>
                                {/* Por segurança o servidor responde "enviado" mesmo para e-mail sem acesso — por isso a dica */}
                                <div style={{ marginTop: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: '0.8rem', color: '#475569', lineHeight: 1.5 }}>
                                    <strong>O código não chegou em 2 minutos?</strong>
                                    <ol style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                        <li>Confira se <strong>{email || 'o e-mail'}</strong> é o mesmo que você usa para <strong>entrar no Quality</strong> — pode ser o seu e-mail pessoal, e não o corporativo (ou o contrário). Para um e-mail sem acesso, nenhum código é enviado.</li>
                                        <li>Olhe a pasta <strong>Spam / Lixo eletrônico</strong> (remetente: Quality Homecare).</li>
                                        <li>Se ainda assim não chegar, fale com o administrador do sistema.</li>
                                    </ol>
                                </div>
                            </div>
                            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                        </>
                    )}
                    <div className="form-group" style={{ marginBottom: '20px' }}>
                        <label style={labelStyle}>Nova senha</label>
                        <input type="password" autoComplete="new-password" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} required minLength={8} style={inputStyle} />
                    </div>
                    <div className="form-group" style={{ marginBottom: '24px' }}>
                        <label style={labelStyle}>Confirmar nova senha</label>
                        <input type="password" autoComplete="new-password" value={confirmar} onChange={(e) => setConfirmar(e.target.value)} required minLength={8} style={inputStyle} />
                    </div>
                    <RequisitosSenha senha={novaSenha} confirmacao={confirmar} />

                    {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', fontWeight: 600, marginBottom: '20px', textAlign: 'center' }}>{error}</p>}

                    <button type="submit" className="btn-login" disabled={loading} style={{ width: '100%', padding: '14px', borderRadius: '12px', background: '#4f46e5', color: '#fff', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}>
                        {loading ? 'Salvando...' : primeiroAcesso ? 'Criar senha e entrar' : 'Salvar nova senha'}
                    </button>
                </form>

                <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.85rem' }}>
                    <a href="/login" style={{ color: '#64748b' }}>Voltar ao login</a>
                </p>
            </div>
        </div>
    );
}

export default NovaSenha;
